import { FC, createContext, useContext, useEffect } from 'react';

import { useField } from 'formik';
import { useRecoilState } from 'recoil';
import { TransactionPricingOfferPayload } from 'types/graphql';

import { transactionOfferAtom } from 'src/atoms';
import {
    useAdditionalTarrifs,
    useComplianceCharges,
    useTransactionCharges,
    useTransactionPricing,
} from 'src/hooks';

import { useServiceOrderContext } from '../ServiceOrderProvider';

interface TransactionChargeProvider {
    setTransactionOfferId: (id: number) => void;
    selectedTransactionOfferId: number;
    selectedActiveTransactionPricingOffer: TransactionPricingOfferPayload;
    refetchActiveTransactionOffers: () => void;
}

const TransactionChargeContext = createContext<Partial<TransactionChargeProvider>>({});

export const useTransactionChargeContext = () => {
    return useContext(TransactionChargeContext);
};

export const TransactionChargeProvider: FC<TransactionChargeProvider> = ({ children }) => {
    const [selectedActiveTransactionPricingOffer, setTransactionPricingOffer] =
        useRecoilState(transactionOfferAtom);

    const { partnerId } = useServiceOrderContext();

    useAdditionalTarrifs();
    useTransactionCharges();
    useComplianceCharges();

    const {
        TRANSACTION_PRICING_OFFER_ID_KEY,
        activeTransactionPricingOffers,
        refetchActiveTransactionOffers,
    } = useTransactionPricing(partnerId);

    const [{ value: transactionOfferId }, __, { setValue: setTransactionOfferId }] = useField(
        `service.${TRANSACTION_PRICING_OFFER_ID_KEY}`
    );
    const [{ value: selectedTransactionOffer }, ___, { setValue: setSeletedTransactionOffer }] =
        useField('service.selectedTransactionOffer');

    useEffect(() => {
        if (transactionOfferId && transactionOfferId !== selectedTransactionOffer?.id) {
            if (activeTransactionPricingOffers?.length) {
                const transactionOffer = activeTransactionPricingOffers.find((el) => {
                    return el.id == transactionOfferId;
                });

                setTransactionPricingOffer(transactionOffer);
                setSeletedTransactionOffer(transactionOffer);
            }
        }
    }, [transactionOfferId, activeTransactionPricingOffers]);

    return (
        <TransactionChargeContext.Provider
            value={{
                setTransactionOfferId,
                selectedTransactionOfferId: transactionOfferId,
                refetchActiveTransactionOffers,
                selectedActiveTransactionPricingOffer,
            }}
        >
            {children}
        </TransactionChargeContext.Provider>
    );
};
